// ============================================================
// SpringBootDeployment.jsx
//
// A branded, responsive course content page for the
// "Global Cloud Deployment" lesson.
//
// Author: ProDev Enterprise Series (Java Branch)
// Features: Railway, AWS (Elastic Beanstalk), Azure (App Service),
//           GCP (App Engine), Docker, and Profile management.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import RocketLaunchIcon       from "@mui/icons-material/RocketLaunch";
import GitHubIcon             from "@mui/icons-material/GitHub";
import TerminalIcon           from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon        from "@mui/icons-material/ContentCopy";
import DoneIcon               from "@mui/icons-material/Done";
import SchoolIcon             from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon          from "@mui/icons-material/ArrowBack";
import CloudIcon              from "@mui/icons-material/Cloud";
import StorageIcon            from "@mui/icons-material/Storage";
import SettingsInputHdmiIcon  from "@mui/icons-material/SettingsInputHdmi";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ title, code }) {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(code).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Typography sx={{ color: BRAND.gold, fontSize: 11, fontWeight: 700, fontFamily: "monospace", textTransform: 'uppercase' }}>{title}</Typography>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5 }}>
                <Typography component="pre" sx={{ fontFamily: "monospace", fontSize: 13, color: BRAND.codeText, whiteSpace: "pre-wrap", m: 0 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function SpringBootDeployment() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (window.pageYOffset > 400) setShowScroll(true);
            else setShowScroll(false);
        };
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<RocketLaunchIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="Java Enterprise · Module 22"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Multi-Cloud <span>Deployment</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Ready for production? Learn how to containerize your Spring Boot app and deploy it across the industry's leading cloud providers: Railway, AWS, Azure, and Google Cloud.
                    </Typography>
                </Box>

                {/* 1. RAILWAY */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<GitHubIcon />} label="1. Railway (GitHub & CLI)" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        The most developer-friendly cloud for Spring Boot. It offers automatic CI/CD from GitHub or direct deployment via terminal.
                    </Typography>
                    <CodeBlock
                        title="Railway CLI Quick-Start"
                        code={`# Install, Login, and Push\nnpm i -g @railway/cli\nrailway login\nrailway up # Detects buildpacks or Dockerfile automatically`}
                    />
                </Box>

                {/* 2. AWS ELASTIC BEANSTALK */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<CloudIcon />} label="2. Amazon Web Services (AWS)" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Deploy using <strong>Elastic Beanstalk</strong> for automated capacity provisioning and health monitoring.
                    </Typography>
                    <CodeBlock
                        title="AWS CLI Deployment"
                        code={`# Package the JAR\nmvn clean package\n\n# Create environment and deploy\neb init -p java-17 my-app\neb create my-env\neb deploy`}
                    />
                </Box>

                {/* 3. AZURE APP SERVICE */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<CloudIcon />} label="3. Microsoft Azure" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Use the <strong>Azure Web App</strong> plugin for Maven to deploy directly from your build tool.
                    </Typography>
                    <CodeBlock
                        title="Azure Maven Plugin"
                        code={`# Add to pom.xml and run:\nmvn com.microsoft.azure:azure-webapp-maven-plugin:2.5.0:config\nmvn package azure-webapp:deploy`}
                    />
                </Box>

                {/* 4. GOOGLE CLOUD (GCP) */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<CloudIcon />} label="4. Google Cloud Platform" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Deploy to <strong>App Engine</strong> or <strong>Cloud Run</strong> (serverless containers).
                    </Typography>
                    <CodeBlock
                        title="GCP App Engine"
                        code={`# Create app.yaml\nruntime: java17\ninstance_class: F1\n\n# Deploy via gcloud SDK\ngcloud app deploy`}
                    />
                </Box>

                {/* 5. DOCKER & CONTAINERS */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StorageIcon />} label="5. Container Strategy" />
                    <Typography sx={{ color: BRAND.muted, mb: 3, fontSize: 14 }}>
                        Docker is the universal language of the cloud. A standard Dockerfile makes your app portable across EKS (AWS), AKS (Azure), and GKE (Google).
                    </Typography>
                    <CodeBlock
                        title="Standard Dockerfile"
                        code={`FROM openjdk:17-jdk-slim\nCOPY target/*.jar app.jar\nENTRYPOINT ["java", "-jar", "/app.jar"]`}
                    />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* SUMMARY */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="Deployment Matrix" />
                    <Grid container spacing={2}>
                        {[
                            { title: "Railway", desc: "Best for startups & rapid CI/CD.", strength: "Simplicity" },
                            { title: "AWS", desc: "Enterprise standard with deep ecosystem.", strength: "Scalability" },
                            { title: "Azure", desc: "Native integration with AD and MS Tools.", strength: "Compatibility" },
                            { title: "GCP", desc: "Leader in Serverless and Kubernetes.", strength: "Speed" }
                        ].map((item, i) => (
                            <Grid item xs={12} sm={6} md={3} key={i}>
                                <Paper sx={{ p: 2, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2, height: '100%' }}>
                                    <Typography sx={{ color: BRAND.gold, fontWeight: 700, mb: 0.5 }}>{item.title}</Typography>
                                    <Typography sx={{ color: BRAND.white, fontSize: 11, mb: 1 }}>{item.strength}</Typography>
                                    <Typography sx={{ color: BRAND.muted, fontSize: 11 }}>{item.desc}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/java-dashboard"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "99px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": { borderColor: BRAND.goldBright, background: BRAND.goldGlow }
                        }}
                    >
                        Return to Hub
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold, flexShrink: 0 }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> Most clouds expect your application to listen on a port provided by an environment variable. In your <code>application.properties</code>, use <code>server.port=&#36;{'{'}PORT:8080{'}'}</code> to ensure compatibility across all providers!
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
